import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { SidenavService } from '@ml/core/services';
import { NotificationsService } from '@ml/notifications/services';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PaginationResponse } from '@ml/shared/types/pagination-response';
import { Notification } from '@ml/notifications/types/interfaces';
import { QueryParams } from '@ml/shared/types/query-params';
import { NotificationType } from '@ml/notifications/types/enums';
import { Router } from '@angular/router';

@Component({
  selector: 'ml-notifications',
  template: `
    <div fxLayout="column" class="content">
      <h3 class="mat-title" fxLayout="row" fxLayoutAlign="space-between center">
        <span>{{ 'notifications' | translate | titlecase }}</span>
        <div fxLayout="row" fxLayoutGap="8px">
          <button mat-button (click)="onClearAll()">
            {{ 'clear-all' | translate | titlecase }}
          </button>
          <button mat-icon-button (click)="onClose()">
            <mat-icon svgIcon="close">clear</mat-icon>
          </button>
        </div>
      </h3>
      <ml-notifications-list
        [notifications]="notifications$ | async"
        [next]="next"
        [loadingMore]="loadingMore"
        (remove)="onRemove($event)"
        (loadMore)="onLoadMore()"
        (redirect)="onRedirect($event)"
      >
      </ml-notifications-list>
    </div>
  `,
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject();
  notifications$: BehaviorSubject<Notification[]> = new BehaviorSubject(null);
  lastQueryParams$: BehaviorSubject<QueryParams>;
  next: boolean;
  loadingMore: boolean;

  constructor(
    private notificationsService: NotificationsService,
    private sidenavService: SidenavService,
    private router: Router
  ) {}

  private static getInitQueryParams() {
    const queryParams = new QueryParams();
    queryParams.pageSize = 20;
    return queryParams;
  }

  ngOnInit() {
    const queryParams = NotificationsComponent.getInitQueryParams();
    this.lastQueryParams$ = new BehaviorSubject<QueryParams>(queryParams);

    this.lastQueryParams$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap(params => this.getNotifications(params))
      )
      .subscribe((response: PaginationResponse<Notification>) => {
        response.next ? (this.next = true) : (this.next = false);
        this.setNotifications(response);
        this.loadingMore = false;
        this.setReadNotifications(response.results);
      });
  }

  onRemove(notification: Notification) {
    this.notificationsService
      .delete(notification.id)
      .pipe(
        tap(() =>
          this.notifications$.next(
            this.notifications$.value.filter(
              _notification => notification.id !== _notification.id
            )
          )
        ),
        tap(() => {
          // If after deleting a notification, there are less than 20 notifications, get the first page again
          if (this.notifications$.value.length < 20) {
            this.lastQueryParams$.next({
              ...this.lastQueryParams$.value,
              page: 1
            });
          }
        })
      )
      .subscribe();
  }

  onLoadMore() {
    this.loadingMore = true;
    const lastQueryParams: QueryParams = this.lastQueryParams$.value;
    lastQueryParams.page++;
    this.lastQueryParams$.next({ ...lastQueryParams });
  }

  onRedirect(notification: Notification) {
    if (notification.type === NotificationType.TASK) {
      this.router.navigate(['tasks', notification.objectId]);
      this.sidenavService.close();
    }
    if (notification.type === NotificationType.ORDER) {
      this.router.navigate(['orders', notification.objectId]);
      this.sidenavService.close();
    }
  }

  onClearAll() {
    if (this.notifications$.value.length === 0) {
      return;
    }
    this.notificationsService
      .clearAll()
      .subscribe(() =>
        this.lastQueryParams$.next(NotificationsComponent.getInitQueryParams())
      );
  }

  onClose() {
    this.sidenavService.close();
  }

  /**
   * Call the list method from notifications service
   */
  private getNotifications(params: QueryParams) {
    return this.notificationsService
      .list(params)
      .pipe(catchError(() => of([])));
  }

  private setNotifications(response: PaginationResponse<Notification>) {
    if (this.notifications$.value && this.lastQueryParams$.value.page !== 1) {
      this.notifications$.next([
        ...this.notifications$.value,
        ...response.results
      ]);
    } else {
      this.notifications$.next(response.results);
    }
  }

  /**
   * Read all unread notifications
   */
  private setReadNotifications(notifications: Notification[]) {
    const unreadNotifications = notifications.filter(item => !item.read);
    if (unreadNotifications.length > 0) {
      this.notificationsService.readAll().subscribe();
    }
    // const requests: Observable<void>[] = this.getUnreadNotificationRequests(
    //   notifications
    // );
    // if (requests.length !== 0) {
    //   combineLatest(requests).subscribe();
    // }
  }

  /* private getUnreadNotificationRequests(notifications: Notification[]) {
     const requests: Observable<void>[] = [];
     notifications.forEach((notification: Notification) => {
       if (!notification.read) {
         requests.push(this.notificationsService.read(notification.id));
       }
     });

     return requests;
   }*/

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
